import {
  BulkCreateShiftmanagementShiftShiftsInput,
  EmployeesEmployeeSelectorInput,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementBulkDeleteShiftsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { toBreaksPayload } from 'modules/shift_management/lib/buildAddPlannedBreaksParameters'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { BulkPatchProps } from './useEditShifts'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface ReassignShiftProps {
  employeeSelection: EmployeesEmployeeSelectorInput
  shifts: Shift[]
  editShiftsParams: BulkPatchProps
  assignShiftsParams: BulkCreateShiftmanagementShiftShiftsInput[]
  isOriginalIncluded: boolean
  plannedBreakForms: PlannedBreakForm[]
  through: 'modal' | 'side_panel'
}

export const useReassignShifts = ({
  employeeSelection,
  shifts,
  editShiftsParams,
  assignShiftsParams,
  isOriginalIncluded,
  plannedBreakForms,
  through,
}: ReassignShiftProps) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()

  const [bulkDestroyShifts, { loading: loadingDestroyShifts }] = useMutation(
    ShiftManagementBulkDeleteShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    }
  )

  const [addShifts, { loading: loadingAddShifts }] = useMutation(
    ShiftManagementBulkCreateShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
    }
  )

  const onReassignShifts = async (closePortal?: ClosePortalFn) => {
    const assignedToModified = true
    const shiftIds = shifts.map((shift) => shift.id)
    // Get all shift rows that have shifts by employees affected by the edit action to update stats
    const shiftsEmployeeIds = uniq(shifts.map((shift: Shift) => shift.employeeId))

    const { name, startAt, endAt, notes, extraHours, locationId, workAreaId, mixedValueNotes } =
      editShiftsParams

    // After the employeeSelectorV2 refactor, we can select 'all' or X employee ids,
    // so we also include the original employee in the selection,
    // so we need to destroy the shifts of the original employee and recreate them.
    const destroyedShiftsResult = await bulkDestroyShifts({
      variables: { ids: shiftIds },
      update: (cache, { data }) => {
        data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
          cache.evict({
            id: cache.identify(shift),
          })
        })
      },
    })

    const destroyedShiftsHandled = handleResult(destroyedShiftsResult, (_errors) => {})
    const destroyedShifts = destroyedShiftsHandled.success
      ? destroyedShiftsHandled.data?.shifts || []
      : []

    const shiftsToDestroy = destroyedShifts.map((shift) =>
      Shift.buildFromGraphQL({
        shift,
        employeeFullName: shift?.employee?.fullName || '',
        employeeAvatar: shift?.employee?.avatar?.url || '',
        employeeLocationId: shift?.employee?.locationId || 0,
      })
    )
    dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: shiftsToDestroy, view })

    const result = await addShifts({
      variables: {
        shifts: assignShiftsParams,
        plannedBreaks: toBreaksPayload(plannedBreakForms),
        employeeSelection: employeeSelection,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      const shiftsData = handled.data?.shifts || []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      )

      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })
      const { shiftsReferenceKeys } = buildEmployeeIds(view, shiftsRows, newShifts)

      const previousShiftsRows = shiftsRows.filter((row) => shiftsReferenceKeys.includes(row.key))
      await refreshStats(previousShiftsRows)

      const totalNumberOfAssignedEmployees = isOriginalIncluded
        ? shiftsEmployeeIds.length + assignShiftsParams.length
        : assignShiftsParams.length

      await trackEditShiftSubmit({
        shifts,
        name,
        startAt,
        endAt,
        notes,
        extraHours,
        locationId,
        workAreaId,
        mixedValueNotes,
        view,
        assignedToModified,
        originalEmployeeIncluded: isOriginalIncluded,
        totalNumberOfAssignedEmployees,
        through,
      })

      if (closePortal) closePortal()

      // Clear selection at the end to not remove the edit shifts button,
      // because the sidepanel and modal are rendered from it.
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.edit_shifts_success'),
      })
    }
  }

  return {
    onReassignShifts,
    submittingOnReassign: loadingDestroyShifts || loadingAddShifts,
  }
}
